import { useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Check,
  X,
  FileText,
  User,
  Phone,
  Calendar,
  ZoomIn,
  Clock,
  Car,
  Hash,
  ArrowLeft,
} from "lucide-react";
import { formatDate } from "@/lib/utils";
import { ROUTES } from "@/constants/routes";
import {
  useVehicle,
  useApproveVehicle,
  useRejectVehicle,
} from "../hooks/useVehicles";
import { PageHeader } from "@/components/common/PageHeader";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Modal,
  ModalHeader,
  ModalTitle,
  ModalBody,
  ModalFooter,
} from "@/components/ui/modal";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { PageSpinner } from "@/components/ui/spinner";
import { EmptyState } from "@/components/ui/empty-state";
import { vehicleCapacityLabel } from "@/constants/vehicles";

function VerificationBadge({ status }: { status?: string }) {
  if (!status) return null;
  const variant =
    status === "approved"
      ? "success"
      : status === "rejected"
        ? "destructive"
        : "warning";
  return (
    <Badge variant={variant} className="text-xs font-semibold">
      {status.charAt(0).toUpperCase() + status.slice(1)}
    </Badge>
  );
}

function InfoRow({
  icon: Icon,
  label,
  value,
}: {
  icon: React.ElementType;
  label: string;
  value: string | number | null | undefined;
}) {
  return (
    <div className="flex items-start gap-3 py-3.5 border-b border-border/40 last:border-0">
      <div className="flex-shrink-0 w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center">
        <Icon className="h-4.5 w-4.5 text-primary" />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
          {label}
        </p>
        <p className="text-sm font-semibold mt-0.5 break-words">
          {value || "—"}
        </p>
      </div>
    </div>
  );
}

function DocumentCard({
  title,
  imageUrl,
  onPreview,
}: {
  title: string;
  imageUrl?: string;
  onPreview: () => void;
}) {
  if (!imageUrl) {
    return (
      <div className="group relative rounded-xl overflow-hidden border border-border bg-card shadow-sm">
        <div className="h-48 bg-muted flex items-center justify-center">
          <span className="text-muted-foreground text-sm">
            No Document Provided
          </span>
        </div>
        <div className="p-3.5 flex items-center gap-2">
          <FileText className="h-4 w-4 text-primary" />
          <p className="font-semibold text-sm">{title}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="group relative rounded-xl overflow-hidden border border-border bg-card shadow-sm hover:shadow-lg transition-all duration-300">
      <div
        className="relative h-48 bg-muted cursor-pointer overflow-hidden"
        onClick={onPreview}
      >
        <img
          src={imageUrl}
          alt={title}
          className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
          onError={(e) => {
            (e.target as HTMLImageElement).src =
              `https://placehold.co/400x250/1e293b/94a3b8?text=${encodeURIComponent(title)}`;
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-200 flex items-end justify-center pb-4">
          <div className="flex items-center gap-1.5 text-white text-sm font-medium bg-black/30 px-3 py-1.5 rounded-full backdrop-blur-sm">
            <ZoomIn className="h-4 w-4" />
            View Full Size
          </div>
        </div>
      </div>
      <div className="p-3.5 flex items-center gap-2">
        <FileText className="h-4 w-4 text-primary" />
        <p className="font-semibold text-sm">{title}</p>
      </div>
    </div>
  );
}

export function VehicleDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [rejectModal, setRejectModal] = useState(false);
  const [rejectReason, setRejectReason] = useState("");
  const [imagePreview, setImagePreview] = useState<string | null>(null);

  const { data: vehicle, isLoading } = useVehicle(id as string);
  const { mutate: approve, isPending: approving } = useApproveVehicle();
  const { mutate: reject, isPending: rejecting } = useRejectVehicle();

  if (isLoading) {
    return <PageSpinner message="Loading vehicle details..." />;
  }

  if (!vehicle) {
    return (
      <div className="space-y-6">
        <PageHeader
          title="Vehicle Not Found"
          breadcrumbs={[
            { label: "Vehicles", href: ROUTES.VEHICLES },
            { label: "Pending", href: ROUTES.VEHICLE_PENDING },
            { label: "Not Found" },
          ]}
        />
        <EmptyState
          icon="default"
          title="Vehicle not found"
          description="The vehicle record you're looking for doesn't exist or could not be loaded."
          action={{
            label: "Back to Pending Vehicles",
            onClick: () => navigate(ROUTES.VEHICLE_PENDING),
          }}
        />
      </div>
    );
  }

  const handleApprove = () => {
    approve(vehicle.id, {
      onSuccess: () => navigate(ROUTES.VEHICLE_PENDING),
    });
  };

  const handleReject = () => {
    if (rejectReason) {
      reject(
        { id: vehicle.id, reason: rejectReason },
        {
          onSuccess: () => {
            setRejectModal(false);
            setRejectReason("");
            navigate(ROUTES.VEHICLE_PENDING);
          },
        },
      );
    }
  };

  // Only show documents that were actually uploaded. Insurance isn't collected
  // during onboarding, so it'd otherwise render as an empty/broken tile.
  const documents = [
    { title: "RC Document", url: vehicle.rc_document_url },
    { title: "Insurance Document", url: vehicle.insurance_document_url },
  ].filter((d) => !!d.url);

  return (
    <div className="space-y-6">
      <PageHeader
        title="Vehicle Verification"
        description={`Review details for ${vehicle.vehicle_model} (${vehicle.registration_number})`}
        breadcrumbs={[
          { label: "Vehicles", href: ROUTES.VEHICLES },
          { label: "Pending", href: ROUTES.VEHICLE_PENDING },
          { label: vehicle.registration_number },
        ]}
        actions={
          <Button
            variant="outline"
            onClick={() => navigate(ROUTES.VEHICLE_PENDING)}
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to List
          </Button>
        }
      />

      {/* Vehicle Header Card */}
      <Card className="overflow-hidden">
        <div className="bg-gradient-to-r from-primary/10 via-primary/5 to-transparent p-6">
          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-5">
            <div className="relative">
              <div className="w-20 h-20 rounded-2xl flex items-center justify-center bg-primary/20 border-2 border-background shadow-lg">
                <Car className="h-10 w-10 text-primary" />
              </div>
            </div>

            <div className="flex-1 min-w-0">
              <h2 className="text-2xl font-bold">{vehicle.vehicle_model}</h2>
              <p className="text-sm font-semibold uppercase text-muted-foreground mt-1">
                {vehicle.registration_number}
              </p>
              <div className="flex flex-wrap items-center gap-2 mt-3">
                <VerificationBadge status={vehicle.verification_status} />
                <Badge variant="outline" className="capitalize">
                  {vehicle.vehicle_type || "Unknown Type"}
                </Badge>
              </div>
            </div>
            {vehicle.verification_status === "pending" && (
              <div className="flex gap-3 w-full sm:w-auto">
                <Button
                  variant="destructive"
                  size="lg"
                  className="flex-1 sm:flex-initial shadow-sm"
                  onClick={() => setRejectModal(true)}
                  disabled={approving}
                >
                  <X className="h-4 w-4 mr-2" />
                  Reject
                </Button>
                <Button
                  variant="success"
                  size="lg"
                  className="flex-1 sm:flex-initial shadow-sm"
                  onClick={handleApprove}
                  isLoading={approving}
                >
                  <Check className="h-4 w-4 mr-2" />
                  Approve
                </Button>
              </div>
            )}
          </div>
        </div>
      </Card>

      <div className="grid gap-6 lg:grid-cols-2">
        {/* Vehicle Information */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base">
              <Car className="h-5 w-5 text-primary" />
              Vehicle Information
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-0">
            <InfoRow
              icon={Car}
              label="Vehicle Model"
              value={vehicle.vehicle_model}
            />
            <InfoRow
              icon={Hash}
              label="Registration Number"
              value={vehicle.registration_number}
            />
            <InfoRow
              icon={Car}
              label="Vehicle Type"
              value={vehicle.vehicle_type}
            />
            <InfoRow
              icon={Hash}
              label="Capacity"
              value={vehicleCapacityLabel(vehicle.vehicle_type)}
            />
            <InfoRow
              icon={FileText}
              label="Insurance Details"
              value={vehicle.insurance_details}
            />
          </CardContent>
        </Card>

        {/* Driver Information */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base">
              <User className="h-5 w-5 text-primary" />
              Driver Information
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-0">
            {vehicle.driver ? (
              <>
                <InfoRow
                  icon={User}
                  label="Driver Name"
                  value={vehicle.driver.user?.name || "Unknown"}
                />
                <InfoRow
                  icon={Phone}
                  label="Phone Number"
                  value={
                    vehicle.driver.user?.phone ||
                    vehicle.driver.user?.mobile_number ||
                    "Unknown"
                  }
                />
                <div className="flex items-start gap-3 py-3.5 border-b border-border/40 last:border-0">
                  <div className="flex-shrink-0 w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center">
                    <Check className="h-4.5 w-4.5 text-primary" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
                      Driver Verification Status
                    </p>
                    <div className="mt-1">
                      <VerificationBadge
                        status={vehicle.driver.verification_status}
                      />
                    </div>
                  </div>
                </div>
              </>
            ) : (
              <EmptyState icon="users" title="No matching driver found" />
            )}
          </CardContent>
        </Card>
      </div>

      {/* Account Timeline */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <Clock className="h-5 w-5 text-primary" />
            Timeline
          </CardTitle>
        </CardHeader>
        <CardContent className="pt-0">
          <div className="grid gap-0 md:grid-cols-2">
            <InfoRow
              icon={Calendar}
              label="Registration Submitted"
              value={formatDate(vehicle.created_at)}
            />
            <InfoRow
              icon={Clock}
              label="Last Updated"
              value={vehicle.updated_at ? formatDate(vehicle.updated_at) : "—"}
            />
          </div>
        </CardContent>
      </Card>

      {/* Documents */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <FileText className="h-5 w-5 text-primary" />
            Uploaded Documents
          </CardTitle>
        </CardHeader>
        <CardContent className="pt-0">
          {documents.length === 0 ? (
            <p className="text-sm text-muted-foreground py-2">No documents uploaded.</p>
          ) : (
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
              {documents.map((doc) => (
                <DocumentCard
                  key={doc.title}
                  title={doc.title}
                  imageUrl={doc.url}
                  onPreview={() => doc.url && setImagePreview(doc.url)}
                />
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Reject Modal */}
      <Modal isOpen={rejectModal} onClose={() => setRejectModal(false)}>
        <ModalHeader onClose={() => setRejectModal(false)}>
          <ModalTitle>Reject Vehicle</ModalTitle>
        </ModalHeader>
        <ModalBody>
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">
              Please provide a reason for rejecting the{" "}
              <strong>{vehicle.vehicle_model}</strong> verification request. The
              driver will be notified.
            </p>
            <div className="space-y-2">
              <Label required>Rejection Reason</Label>
              <Textarea
                value={rejectReason}
                onChange={(e) => setRejectReason(e.target.value)}
                placeholder="Enter reason for rejection..."
                rows={4}
              />
            </div>
          </div>
        </ModalBody>
        <ModalFooter>
          <Button variant="outline" onClick={() => setRejectModal(false)}>
            Cancel
          </Button>
          <Button
            variant="destructive"
            onClick={handleReject}
            isLoading={rejecting}
            disabled={!rejectReason.trim()}
          >
            Reject Vehicle
          </Button>
        </ModalFooter>
      </Modal>

      {/* Image Preview Modal */}
      <Modal isOpen={!!imagePreview} onClose={() => setImagePreview(null)}>
        <ModalHeader onClose={() => setImagePreview(null)}>
          <ModalTitle>Document Preview</ModalTitle>
        </ModalHeader>
        <ModalBody>
          {imagePreview && (
            <img
              src={imagePreview}
              alt="Document"
              className="w-full rounded-lg"
              onError={(e) => {
                (e.target as HTMLImageElement).src =
                  "https://placehold.co/600x400/1e293b/94a3b8?text=Image+Not+Available";
              }}
            />
          )}
        </ModalBody>
      </Modal>
    </div>
  );
}
